import { useEffect, useState, useCallback } from 'react';
import { FaPlus } from 'react-icons/fa';
import api from '../../services/api';
import styles from './Genres.module.css';

const Genres = () => {
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, booksCount }

  const fetchGenres = useCallback(() => {
    setLoading(true);
    api.get('/genres').then((res) => {
      setGenres(res.data?.genres || []);
    }).catch(() => setGenres([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchGenres(); }, [fetchGenres]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setError('');
    setAdding(true);
    try {
      await api.post('/genres', { name });
      setNewName('');
      fetchGenres();
    } catch (err) {
      setError(err.response?.data?.message === 'genre already exists'
        ? 'Такой жанр уже существует'
        : 'Ошибка при добавлении жанра');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (genre) => {
    setEditingId(genre.id);
    setEditValue(genre.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = async (id) => {
    const name = editValue.trim();
    if (!name) return;
    try {
      await api.put(`/genres/${id}`, { name });
      setEditingId(null);
      fetchGenres();
    } catch (err) {
      setError(err.response?.data?.message === 'genre already exists'
        ? 'Такой жанр уже существует'
        : 'Ошибка при изменении жанра');
    }
  };

  const requestDelete = async (genre) => {
    setError('');
    try {
      await api.delete(`/genres/${genre.id}`);
      fetchGenres();
    } catch (err) {
      const booksCount = err.response?.data?.booksCount;
      if (err.response?.status === 409 && typeof booksCount === 'number') {
        setDeleteTarget({ id: genre.id, name: genre.name, booksCount });
      } else {
        setError('Ошибка при удалении жанра');
      }
    }
  };

  const confirmForceDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/genres/${deleteTarget.id}`, { params: { force: true } });
      setDeleteTarget(null);
      fetchGenres();
    } catch (err) {
      setError('Ошибка при удалении жанра');
      setDeleteTarget(null);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.totalCount}>Всего жанров: {genres.length}</div>
      </div>

      <form className={styles.addBar} onSubmit={handleAdd}>
        <input
          className={styles.addInput}
          placeholder="Название нового жанра..."
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <button type="submit" className={styles.addBtn} disabled={adding || !newName.trim()}>
          <FaPlus /> Добавить
        </button>
      </form>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.tableWrap}>
        {loading ? (
          <div className={styles.loading}>Загрузка...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Название</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {genres.length === 0 ? (
                <tr><td colSpan={2}><div className={styles.empty}>Жанры не найдены</div></td></tr>
              ) : genres.map((genre) => (
                <tr key={genre.id}>
                  <td>
                    {editingId === genre.id ? (
                      <input
                        className={styles.nameInput}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        autoFocus
                      />
                    ) : (
                      genre.name
                    )}
                  </td>
                  <td>
                    <div className={styles.actions}>
                      {editingId === genre.id ? (
                        <>
                          <button className={styles.editBtn} onClick={() => saveEdit(genre.id)}>Сохранить</button>
                          <button className={styles.cancelBtn} onClick={cancelEdit}>Отмена</button>
                        </>
                      ) : (
                        <>
                          <button className={styles.editBtn} onClick={() => startEdit(genre)}>Изменить</button>
                          <button className={styles.deleteBtn} onClick={() => requestDelete(genre)}>Удалить</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {deleteTarget && (
        <div className={styles.modal}>
          <div className={styles.modalBox}>
            <div className={styles.modalTitle}>Удалить жанр «{deleteTarget.name}»?</div>
            <div className={styles.modalText}>
              Этот жанр используется в {deleteTarget.booksCount}{' '}
              {deleteTarget.booksCount === 1 ? 'книге' : 'книгах'}. При удалении он будет убран из этих книг.
              Это действие нельзя отменить.
            </div>
            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setDeleteTarget(null)}>Отмена</button>
              <button className={styles.confirmDeleteBtn} onClick={confirmForceDelete}>Удалить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Genres;
